import { ReactElement, useState } from 'react'
import { Button, Popover, Typography, Dialog, Tooltip } from '@equinor/eds-core-react'
import styled from 'styled-components'
import Icon from '@/styles/icon'
import { close as closeIcon, delete_to_trash as deleteIcon } from '@equinor/eds-icons'
import { Sensor, isSensorEditable, getSensorEditDisabledReason } from '../../models'
import { EdsStyledIcon } from '@/styles/layout'

type Props = {
    onClose: () => void
    sensor: Sensor | null
    onEditSensor?: (sensor: Sensor) => void
    onDeleteSensor?: (sensor: Sensor) => void
    onConfigureSensor?: (sensor: Sensor) => void
    hasDownlinkCapability?: boolean
    anchor: HTMLElement | null
}

function OptionsMenu({
    onClose,
    sensor,
    onEditSensor,
    onDeleteSensor,
    onConfigureSensor,
    hasDownlinkCapability,
    anchor,
}: Props): ReactElement {
    const [optionMenuOpen, setOptionMenuOpen] = useState<boolean>(true)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

    const canManage = isSensorEditable(sensor)
    const disabledReason = canManage ? '' : getSensorEditDisabledReason(sensor)

    const handleClose = () => {
        setOptionMenuOpen(false)
        onClose()
        setDeleteDialogOpen(false)
    }

    const handleEditSensor = () => {
        if (!canManage || !sensor) return
        handleClose()
        onEditSensor?.(sensor)
    }

    const handleConfigureSensor = () => {
        if (!canManage || !sensor) return
        handleClose()
        onConfigureSensor?.(sensor)
    }

    const handleOpenDeleteDialog = () => {
        if (!canManage) return
        setDeleteDialogOpen(true)
    }

    const handleDeleteSensor = () => {
        if (!canManage || !sensor) return
        onDeleteSensor?.(sensor)
        setDeleteDialogOpen(false)
        setOptionMenuOpen(false)
    }

    return (
        <div>
            <Popover open={optionMenuOpen} anchorEl={anchor}>
                <Popover.Header>
                    <Popover.Title>Options</Popover.Title>
                    <Button
                        style={{ height: '32px', width: '32px' }}
                        variant="ghost_icon"
                        aria-label="Close popover"
                        onClick={handleClose}
                    >
                        <Icon data={closeIcon} size={24} />
                    </Button>
                </Popover.Header>

                <Popover.Content>
                    <StyledMenuContainer>
                        <Tooltip title={disabledReason} placement="top">
                            <MenuItemSlot>
                                <StyledMenuItem
                                    variant="ghost"
                                    role="menuitem"
                                    disabled={!canManage}
                                    onClick={canManage ? handleEditSensor : undefined}
                                >
                                    <StyledIcon name="edit" size={24} />
                                    <StyledTypography>Edit sensor</StyledTypography>
                                </StyledMenuItem>
                            </MenuItemSlot>
                        </Tooltip>

                        {hasDownlinkCapability && (
                            <Tooltip title={disabledReason} placement="top">
                                <MenuItemSlot>
                                    <StyledMenuItem
                                        variant="ghost"
                                        role="menuitem"
                                        disabled={!canManage}
                                        onClick={canManage ? handleConfigureSensor : undefined}
                                    >
                                        <StyledIcon name="settings" size={24} />
                                        <StyledTypography>Change configuration</StyledTypography>
                                    </StyledMenuItem>
                                </MenuItemSlot>
                            </Tooltip>
                        )}

                        <Tooltip title={disabledReason} placement="top">
                            <MenuItemSlot>
                                <StyledMenuItem
                                    variant="ghost"
                                    role="menuitem"
                                    disabled={!canManage}
                                    onClick={canManage ? handleOpenDeleteDialog : undefined}
                                >
                                    <StyledIcon name="deleteToTrash" size={24} />
                                    <StyledTypography>Delete sensor</StyledTypography>
                                </StyledMenuItem>
                            </MenuItemSlot>
                        </Tooltip>
                    </StyledMenuContainer>
                </Popover.Content>
            </Popover>

            <StyledDeleteDialog
                open={deleteDialogOpen}
                isDismissable={false}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <Dialog.Header>
                    <Dialog.Title>Are you sure you want to delete this sensor?</Dialog.Title>
                </Dialog.Header>

                <Dialog.Content>
                    <Typography variant="body_short">
                        Deleting this sensor will permanently remove all data it has collected. This action cannot be
                        undone.
                    </Typography>
                </Dialog.Content>

                <Dialog.Actions>
                    <ButtonWrapper>
                        <StyledDeleteButton color="danger" onClick={handleDeleteSensor} disabled={!canManage}>
                            Delete
                        </StyledDeleteButton>

                        <StyledDeleteButton variant="ghost" color="danger" onClick={handleClose}>
                            Cancel
                        </StyledDeleteButton>
                    </ButtonWrapper>
                </Dialog.Actions>
            </StyledDeleteDialog>
        </div>
    )
}

const StyledTypography = styled(Typography)`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
`

const StyledIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #6f6f6f;
`

const StyledMenuContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

// Tooltips need a non-disabled wrapper, since disabled buttons do not emit pointer events.
const MenuItemSlot = styled.span`
    display: flex;
    width: 100%;
`
const StyledMenuItem = styled(Button)`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: row;
    gap: 8px;
    width: 100%;
    height: 48px;
    padding: 0 16px;

    &:disabled {
        ${StyledIcon},
        ${StyledTypography} {
            color: inherit;
        }
    }
`

const StyledDeleteDialog = styled(Dialog)`
    display: flex;
    flex-wrap: wrap;
    width: 480px;
    height: 190px;
    gap: 16px;
    @media (max-width: 1024px) {
        max-width: 360px;
    }
`

const StyledDeleteButton = styled(Button)`
    width: 72px;
    border-radius: 4px;
    border: 1px solid;
`

const ButtonWrapper = styled.div`
    display: flex;
    margin-left: 286px;
    gap: 16px;
    margin-top: -24px;
    @media (max-width: 1024px) {
        margin-left: 166px;
    }
`

export default OptionsMenu
